import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import assetsIcon from "../../../assets/images/solar_money-bag-bold-removebg-preview.png";
import * as constants from "../../../constants";
import { setParticipantDetails } from "../../../store/features/auth/fundParticipantsSlice";
import { setNewFundDetails } from "../../../store/features/auth/newFundSlice";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import { getAssetSummaryByPId } from "../services/Summary/accountSummaryService";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";
import { formatCurrency } from "../Utils/formatUtils";
import TableRowSummaryDesign from "./SummaryTableRowDesign";

function Assets() {
  const [showMore, setShowMore] = useState(false);
  const [cashAccount, setCashAccount] = useState<any>({});
  const [participantfunds, setParticipantFunds] = useState<any>([]);
  const [totalAmountPid, setTotalAmountPid] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const participantDetail = useSelector(
    (state: RootState) => state.participantDetails
  );
  const userInfo = useSelector((state: RootState) => state.auth);
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAstByPid(userInfo.defaultParticipantId, userInfo.clientId);
  }, [participantDetail.participantId, userInfo?.defaultParticipantId]);

  function handleEachFund(e: React.MouseEvent<HTMLTableCellElement>, id: any) {
    e.preventDefault();
    navigate("/fundSummary");
    dispatch(
      setNewFundDetails({
        fundId: id,
        page: 0,
        rowsPerPage: 5,
        transactionPage: 0,
        transactionRowsPerPage: 5,
      })
    );
  }
  function summaryDetailsPage(
    e: React.MouseEvent<HTMLTableCellElement>,
    bnkDetails: any
  ) {
    e.preventDefault();
    navigate("/summaryDetails", { state: { bnkDetails } });
  }

  async function getAstByPid(pId: number, clientId: number) {
    try {
      setLoading(true);
      const resAssetByPid = await getAssetSummaryByPId(pId, clientId);
      if (resAssetByPid.status === constants.STATUS_CODES.SUCCESS) {
        setCashAccount(resAssetByPid.data.data.cashAccounts);
        setParticipantFunds(resAssetByPid.data.data.funds);
        dispatch(
          setParticipantDetails({ funds: resAssetByPid.data.data.funds })
        );
        setTotalAmountPid(resAssetByPid.data.data.totalBalance);
      }
    } catch (error: any) {
      setTotalAmountPid(0);
      setCashAccount({});
      setParticipantFunds([]);
      setShowMore(false);
      if (error instanceof AxiosError) {
        console.error(
          translate("axiosError"),
          error.response?.data || error.message
        );
      } else {
        console.error(translate("unexpectedError:"), error);
      }
    } finally {
      setLoading(false);
    }
  }
  return (
    <>
      <Paper
        sx={{
          display: "flex",
          backgroundColor: "transparent",
          boxShadow: "none",
        }}
      >
        <Box className={`${basestyle.assetsContainer}`}>
          <Box className={`${basestyle.circleWhite}`}>
            <img
              src={assetsIcon}
              alt="Asset Logo"
              className="assetLogo"
              style={{ width: "30px", height: "auto" }}
            />
          </Box>
          <Typography variant="h5" className={`${basestyle.assetsText}`}>
            {translate("assets")}
          </Typography>
        </Box>

        {loading ? (
          <Box className="loadingContainer">
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ marginLeft: 1 }}>
              Loading...
            </Typography>
          </Box>
        ) : (<TableContainer
          className={basestyle.mainTableContainer}
          sx={{ border: "none" }}
        >
          <Table size="small" sx={{ borderCollapse: "collapse" }}>
            <TableBody>
              {!loading &&
                <TableRow sx={{ border: "none" }}>
                  <TableCell
                    className={`${basestyle.tableCellStyle}`}
                    sx={{ border: "none" }}
                  >
                    {ClientDetails?.bankSweepApplicable === constants.IS_ACTIVE_YES ? (
                      <Typography
                        onClick={(e: any) => summaryDetailsPage(e, null)}
                        sx={{ color: "blue", cursor: "pointer", fontSize: "16px" }}
                      >
                        {cashAccount?.fundName || "Checking"}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: "16px" }}>
                        {cashAccount?.fundName || "Checking"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ border: "none" }}></TableCell>
                  <TableCell
                    align="right"
                    className={`${basestyle.amountText} ${basestyle.tableCellStyle}`}
                    sx={{ border: "none" }}
                  >
                    {formatCurrency(cashAccount?.availableBalance || 0)}
                  </TableCell>
                </TableRow>}

              {participantfunds && participantfunds.length > 0
                ? participantfunds
                  .filter((item: any) => item.isActive === constants.IS_ACTIVE_YES)
                  .map((row: any) => (
                    <TableRowSummaryDesign key={row.fundId} sx={{ border: "none" }}>
                      <TableCell
                        className={`${basestyle.tableCellStyle}`}
                        sx={{
                          fontSize: "16px",
                          cursor: "pointer",
                          border: "none",
                          color: "blue",
                        }}
                        onClick={(e) => handleEachFund(e, row.fundId)}
                      >
                        {row.fundName} (Sav)
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          fontSize: "16px",
                        }}
                      >
                        {row.description}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={`${basestyle.amountText} ${basestyle.tableCellStyle}`}
                        sx={{ border: "none", fontSize: "16px" }}
                      >
                        {formatCurrency(row?.availableBalance)}
                      </TableCell>
                    </TableRowSummaryDesign>
                  ))
                : (
                  <TableRow sx={{ border: "none" }}>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontSize: "16px",
                        textAlign: "center",
                        border: "none",
                        color: "white",
                        paddingTop: 3
                      }}
                    >
                    </TableCell>
                  </TableRow>
                )
              }


              {showMore &&
                participantfunds?.length > 0 &&
                participantfunds
                  .filter(
                    (item: any) => item.isActive === constants.IS_ACTIVE_NO
                  )
                  .map((row: any) => (
                    <TableRowSummaryDesign key={row.fundId}>
                      <TableCell
                        className={`${basestyle.tableCellStyle}`}
                        sx={{
                          fontSize: "16px",
                          color: "gray",
                          border: "none",
                          cursor: "pointer",
                        }}
                        onClick={(e) => handleEachFund(e, row.fundId)}
                      >
                        {row.fundName} (Sav)
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          fontSize: "16px",
                        }}
                      >
                        {row.description}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={`${basestyle.tableCellStyle}`}
                        sx={{ color: "gray", border: "none", fontSize: "16px" }}
                      >
                        {formatCurrency(row?.availableBalance)}
                      </TableCell>
                    </TableRowSummaryDesign>
                  ))}

              {participantfunds.length > 0 &&
                participantfunds.filter(
                  (item: any) => item.isActive === constants.IS_ACTIVE_NO
                ).length > 0 && (
                  <TableRow sx={{ border: "none" }}>
                    <TableCell colSpan={1.5}
                      sx={{ border: "none" }}></TableCell>
                    <TableCell
                      align="center"
                      className={`${basestyle.tableCellStyle}`}
                      sx={{ border: "none" }}
                    >
                      <Button
                        onClick={() => setShowMore(!showMore)}
                        variant="text"
                        sx={{ color: "#E79A4D" }}
                      >
                        {showMore ? "... Less" : "Show Inactive Funds ... "}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              <TableRow className={`${basestyle.totalAmountStyle}`}>
                <TableCell
                  className={`${basestyle.tableCellStyle}`}
                  sx={{ border: "none" }}
                ></TableCell>
                <TableCell
                  className={`${basestyle.tableCellStyle}`}
                  sx={{ border: "none" }}
                ></TableCell>
                <TableCell
                  align="right"
                  className={`${basestyle.tableCellStyle}`}
                  sx={{ border: "none" }}
                >
                  {!loading &&
                    <Typography>
                      <span className={basestyle.labelBoldText}>{translate("totalAvailableBalance")}</span>&nbsp;
                      <span
                        className={`${basestyle.amountTotalAvaliable}`}
                        style={{ display: "inline-block", fontWeight: "bold" }}
                      >
                        {formatCurrency(totalAmountPid)}
                      </span>
                    </Typography>}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
    </>
  );
}
export default Assets;
